import React from "react";
import { Play, Music2, ListMusic } from "lucide-react";
import { PART_GUIDES } from "@/data/voiceParts";
import { playPitch, playSequence } from "@/lib/audio";

const MAJOR = [0, 2, 4, 5, 7];
const NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
const toMidi = (p) => (Number(p.slice(-1)) + 1) * 12 + NAMES.indexOf(p.slice(0, -1));
const toName = (m) => `${NAMES[m % 12]}${Math.floor(m / 12) - 1}`;
const fiveNote = (start) => { const up = MAJOR.map((s) => toName(toMidi(start) + s)); return [...up, ...up.slice(0, -1).reverse()]; };
const sweep = (start) => [0, 7, 12, 7, 0].map((s) => toName(toMidi(start) + s));
const fit = (start, low, high) => {
  let m = toMidi(start);
  if (low != null && m < low + 1) m = low + 1;
  if (high != null && m + 7 > high) m = Math.max(low != null ? low : m - 12, high - 7);
  return toName(m);
};

export const PartGuide = ({ part, low, high, testId = "part-guide" }) => {
  const g = PART_GUIDES[part];
  if (!g) return null;
  return (
    <div className="mt-4 rounded-2xl border border-border bg-background/60 p-4 sm:p-5" data-testid={testId}>
      <p className="font-mono text-[11px] uppercase tracking-widest text-primary">Your part: {part}</p>
      <p className="mt-2 text-sm text-foreground">{g.about}</p>
      <p className="mt-2 text-sm text-muted-foreground"><strong className="text-foreground">Clef:</strong> {g.clef}</p>
      <p className="mt-4 flex items-center gap-1.5 text-sm font-semibold"><Music2 size={14} className="text-primary" /> 3 warm-ups for you</p>
      <ul className="mt-2 space-y-2">
        {g.warmups.map((w0, k) => ({ ...w0, start: fit(w0.start, low, high) })).map((w, k) => (
          <li key={w.name} className="flex flex-wrap items-center gap-2 rounded-xl border border-border px-3 py-2 text-sm" data-testid={`${testId}-warmup-${k}`}>
            <span className="font-medium">{w.name}</span>
            <span className="text-muted-foreground">— {w.how} Start on {w.start}.</span>
            <span className="ml-auto flex gap-1.5">
              <button onClick={() => playPitch(w.start)} className="inline-flex items-center gap-1 rounded-full border border-primary/40 px-2.5 py-1 text-xs text-primary hover:bg-primary/10" data-testid={`${testId}-warmup-${k}-note`}><Play size={11} /> Starting note</button>
              <button onClick={() => playSequence(w.name.includes("siren") ? sweep(w.start) : fiveNote(w.start), 0.45, 0.4)} className="inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-xs text-muted-foreground hover:border-primary" data-testid={`${testId}-warmup-${k}-pattern`}><Play size={11} /> Pattern</button>
            </span>
          </li>
        ))}
      </ul>
      <p className="mt-4 flex items-center gap-1.5 text-sm font-semibold"><ListMusic size={14} className="text-primary" /> Songs that suit your range</p>
      <ul className="mt-1 list-disc pl-5 text-sm text-muted-foreground" data-testid={`${testId}-songs`}>
        {g.songs.map((s) => <li key={s}>{s}</li>)}
      </ul>
      <p className="mt-2 text-[11px] text-muted-foreground">Pick a key that feels comfortable — your teacher can help you find it.</p>
    </div>
  );
};
